import { Dictionary, IsNever, ValueOf, MaybePromise, values } from '@direct-frontend/stdlib';

import { ErrorsMap, FieldValidationMeta, FieldValidationErrorsMap, FormValidationMeta } from './store/types';

/** Источник возникновения ошибки валидации */
export type ValidationErrorSource =
    // Ошибка была добавлена из валидатора формы
    | { type: 'form' }
    // Ошибка была добавлена из валидатора поля
    | { type: 'field'; fieldKey: string };

/** Метаданные ошибки валидации */
type ValidationErrorMeta = {
    source: ValidationErrorSource;
};

/**
 * Ошибка валидации формы или отдельного поля
 *
 * @typeParam Code Код ошибки
 * @typeParam Params Параметры ошибки. Могут быть как примитивом, так и объектом
 *
 * @example
 * ```typescript
 * import { ValidationError } from '@adv-frontend/form-kit';
 *
 * type FieldValidationError =
 *   | ValidationError<'NAME_MAX_LENGTH', { maxLength: number }>
 *   | ValidationError<'INVALID_VALUE'>;
 * ```
 **/
export type ValidationError<Code extends string, Params = never> = IsNever<
    Params,
    { code: Code; meta: ValidationErrorMeta },
    { code: Code; params: Params; meta: ValidationErrorMeta }
>;

/** Базовый тип для любой ошибки */
export type BaseValidationError = ValidationError<string, never>;

/** Базовый тип для любой ошибки при сабмите формы */
export interface BaseSubmitError {
    code: string;
    params?: unknown;
}

/** Тип валидатора */
export type ValidatorType = ValueOf<typeof ValidatorType>;
export const ValidatorType = {
    onChange: 'onChange',
    onBlur: 'onBlur',
    onSubmit: 'onSubmit',
} as const;
export const AllValidatorTypes = values(ValidatorType);

/** Причина запуска валидации */
export type ValidationCause =
    | 'change' // Изменение примитивного значения
    | 'blur' // Блюр контрола для примитивного значения
    | 'submit' // Сабмит формы
    | 'revalidate'; // Повторный запуск валидаторов (change + blur)

/** Промежуточное состояние запуска валидации формы */
export type ValidationState<FormValidationError extends BaseValidationError> = {
    /** Объект с ошибками формы */
    formValidationErrorsMap: ErrorsMap<FormValidationError>;
    /** Объект с метаинформацией валидации формы  */
    formValidationMeta: FormValidationMeta;
    /**  */
    fieldsValidation: Dictionary<{
        /** Метаданные валидации */
        meta: FieldValidationMeta;
        /** Объект с ошибками */
        errorsMap: FieldValidationErrorsMap;
    }>;
};

/**
 * Тип значения для сравнения при валидации.
 *
 * Три основных источника значения для сравнения:
 * 1. Константы
 * 2. Контекст поля
 * 3. Запросы
 * В рамках готовых валидаторов нужно поддерживать все эти сценарии
 */
export type ValidationComparisonValue<Value, FieldContext> = Value | ((context: FieldContext) => MaybePromise<Value>);
